import {CartData, CartProcessor} from "./CartProcessor";
import {Events} from "./CartEvents";
import {CartFeedback} from "./CartFeedback";
import {countText} from "./countText";
import {cartItemCount, cartSubtotal} from "./cartSummary";

/**
 * The mini cart of a component page as a bar at the bottom of the window (snippets/cart.php).
 *
 * The snippet prints the bar and its panel hidden; MiniCart.ts fills both from every answer of
 * the cart and shows them while the cart holds something. This class adds what makes it a bar:
 *
 * - the container moves to the end of <body>, so `position: fixed` works inside any template (a
 *   transformed ancestor would make fixed relative to it), like the buy bar;
 * - one bar at the bottom (the maintainer's answer 63): on an event page with the buy bar the
 *   toggle moves into the place at the start of the buy bar (`.eventgallery-buy-bar-cart`) and
 *   becomes its cart button - the glyph, the count and the subtotal; only the glyph and the count
 *   in the buy mode (components/bar.less) -, shown while the cart holds something; the bar of the
 *   mini cart stays hidden then. Without that place (no buy bar, an override of the buy bar from
 *   before) the bar stands at the bottom as before. The answer of the cart comes after the first
 *   paint, and the button would push the words of the buy mode aside then: the count and the
 *   subtotal of the last answer in this tab (the session storage, `eventgallery.cart.summary`)
 *   show it from the start, and the answer corrects them (a press before it opens the panel with
 *   it). While a dialog of the page is open (the format dialog which put the first item into the
 *   cart) the button comes or goes when it closes, so the buy bar behind it does not change on
 *   its own;
 * - the height of the bar is published as `--eg-cart-bar-height` on the root, 0 while it hides
 *   (always 0 with the cart button in the buy bar): the bar stands on top of the buy bar
 *   (`--eg-buy-bar-height`), the panel above both, and the page keeps its end clear of both
 *   (mini-cart.less);
 * - the toggle opens the panel, a native <dialog> opened with showModal(): a bottom sheet on a
 *   phone, from 576 px a panel above the cart button at the start of the bar (components/dialog.less,
 *   the maintainer's answer 78). The browser
 *   moves the focus into it, keeps it there and closes it on Escape; this class closes it on a
 *   click beside it, on the close button, before the lightbox opens one of its thumbnails and when
 *   the cart becomes empty, and gives the focus back to the toggle - after the last item, which
 *   takes the toggle away, to the primary button of the buy bar or the main heading of the page;
 * - the toggle is named by its own words, `data-label` ("Your Cart, items: 3, 21.60 EUR";
 *   `data-label-one` for one item) - in the buy mode it shows only the glyph and the count -, a
 *   toggle without them by the sentence of the cart; the name holds what the button shows, the
 *   count right before the subtotal (WCAG 2.5.3 Label in Name); the live region beside the bar
 *   says every change as before (MiniCart.ts);
 * - *View cart* waits while a change of the cart is on its way to the server, like the button of
 *   the format dialog: leaving the page would cancel the request;
 * - the bar steps back while the card of a cart module of the page can be seen (the maintainer's
 *   answer 59): the window shows the cart once. The module stands where the site owner put it -
 *   high in a sidebar on a desktop, near the end of the page on a phone -, and the bar is there
 *   wherever the card is not. The cart button in the buy bar steps back alone and keeps its
 *   room (`is-stepped-back`), so the buy bar stays and nothing in it moves. It never goes while
 *   its panel is open or its toggle has the focus, but once they let go; a browser without
 *   IntersectionObserver keeps it.
 *
 * Nothing of the cart is printed by the server, so the page can come from Joomla's page cache.
 */
export class CartBar {
    static CONTAINER_SELECTOR = '.eventgallery-minicart-container';
    static TOGGLE_SELECTOR = '.eventgallery-minicart-toggle';
    static PANEL_SELECTOR = '.eventgallery-minicart-panel';
    static CLOSE_SELECTOR = '.eventgallery-minicart-close';
    static OPEN_CART_SELECTOR = '.eventgallery-minicart-opencart';
    static STATUS_SELECTOR = '.eventgallery-minicart-status';
    static REMOVE_SELECTOR = '.eventgallery-removeFromCart';
    /** the place of the cart button at the start of the buy bar (snippets/imageset/orderimages.php) */
    static PLACE_SELECTOR = '.eventgallery-buy-bar-cart';
    static COUNT_SELECTOR = '.item-count';
    static SUBTOTAL_SELECTOR = '.cart-subtotal';
    static STEPPED_BACK_CLASS = 'is-stepped-back';
    /** the count and the subtotal of the last answer of the cart in this tab */
    static MEMORY_KEY = 'eventgallery.cart.summary';
    static HEIGHT_PROPERTY = '--eg-cart-bar-height';

    container: HTMLElement;
    bar: HTMLElement | null;
    toggle: HTMLButtonElement;
    panel: HTMLDialogElement;
    status: HTMLElement | null;
    cartProcessor: CartProcessor;
    /** the place in the buy bar where the toggle stands as its cart button, null for the bar of its own */
    place: HTMLElement | null = null;
    /** the place of the remove button which had the focus, so a removed item hands it on */
    private focusedRemove: number = -1;
    private pendingHref: string | null = null;
    /** the cart modules of the page which can be seen now */
    private modulesInView: Set<Element> = new Set();
    /** the cart holds something (the cart button of the buy bar shows) */
    private filled: boolean = false;
    /** the cart button was pressed before the first answer of the cart */
    private openWithTheAnswer: boolean = false;
    /** the dialogs of the page whose close the cart button waits for */
    private awaited: WeakSet<Element> = new WeakSet();

    /**
     * True for the container of the bar; the module and an override of the card of 6.x or of
     * 7.0.0 before the bar have no toggle and keep their place in the page.
     */
    static isBar(container: Element | null): boolean {
        return !!container && !!container.querySelector(CartBar.TOGGLE_SELECTOR) && !!container.querySelector(CartBar.PANEL_SELECTOR);
    }

    constructor(cartProcessor: CartProcessor, container: HTMLElement) {
        this.cartProcessor = cartProcessor;
        this.container = container;
        this.toggle = container.querySelector(CartBar.TOGGLE_SELECTOR);
        this.panel = container.querySelector(CartBar.PANEL_SELECTOR);
        this.bar = this.toggle ? this.toggle.closest('.eg-bar') as HTMLElement : null;
        this.status = container.querySelector(CartBar.STATUS_SELECTOR);

        // fixed to the viewport, so it must not stay inside a positioned or transformed ancestor
        if (container.parentElement !== document.body) {
            document.body.appendChild(container);
        }

        // one bar at the bottom: the toggle becomes the cart button of the buy bar, shown while the
        // cart holds something (update()), and the bar of its own stays hidden
        const place = document.querySelector<HTMLElement>(CartBar.PLACE_SELECTOR);
        if (place && this.bar && this.toggle) {
            this.place = place;
            this.toggle.hidden = true;
            place.appendChild(this.toggle);
            this.bar.hidden = true;
            const remembered = CartBar.remembered();
            if (remembered) {
                this.fill(remembered.count, remembered.subtotal);
            }
        }

        this.publishHeight();
        if (this.bar && typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(() => this.publishHeight()).observe(this.bar);
        }

        this.toggle.addEventListener('click', (e: Event) => {
            e.preventDefault();
            this.open();
        });

        // a click on the backdrop of the dialog - beside the panel - closes it
        this.panel.addEventListener('click', (e: Event) => {
            if (e.target === this.panel) {
                this.close();
            }
        });
        this.panel.querySelectorAll(CartBar.CLOSE_SELECTOR).forEach((button) => {
            button.addEventListener('click', (e: Event) => {
                e.preventDefault();
                this.close();
            });
        });

        // Escape and the back gesture of a phone close the dialog without us
        this.panel.addEventListener('close', () => {
            if (this.toggle.getAttribute('aria-expanded') === 'true') {
                this.closed();
            }
        });

        // A thumbnail opens the lightbox, which is no part of the top layer: the panel goes first,
        // in the capture phase, before the handler of the link sees the click.
        this.panel.addEventListener('click', (e: Event) => {
            const link = (e.target as Element)?.closest?.('a[data-eg-lightbox]');
            if (link && this.panel.contains(link)) {
                this.close();
            }
        }, true);

        // the remove button which has the focus: the items are drawn anew with every answer
        this.panel.addEventListener('focusin', (e: Event) => {
            const removes = Array.from(this.panel.querySelectorAll(CartBar.REMOVE_SELECTOR));
            this.focusedRemove = removes.indexOf(e.target as Element);
        });

        this.panel.querySelector(CartBar.OPEN_CART_SELECTOR)?.addEventListener('click', (e: Event) => this.openCart(e));
        document.addEventListener(Events.busy, (e: Event) => {
            if (!(e as CustomEvent).detail?.busy && this.pendingHref) {
                const href = this.pendingHref;
                this.pendingHref = null;
                document.location.href = href;
            }
        });

        document.addEventListener(Events.reload, (e: Event) => this.update((e as CustomEvent).detail));

        this.watchTheModules();
    }

    /**
     * Watches the cart modules of the page: every container of a mini cart which is not this bar
     * (CartBar.isBar()), the module or an old card of the component.
     */
    private watchTheModules() {
        const modules = Array.from(document.querySelectorAll(CartBar.CONTAINER_SELECTOR))
            .filter((element) => element !== this.container && !CartBar.isBar(element));
        if (!this.bar || modules.length === 0 || typeof IntersectionObserver === 'undefined') {
            return;
        }
        const observer = new IntersectionObserver((entries) => {
            for (const entry of entries) {
                if (entry.isIntersecting) {
                    this.modulesInView.add(entry.target);
                } else {
                    this.modulesInView.delete(entry.target);
                }
            }
            this.stepBack();
        });
        modules.forEach((module) => observer.observe(module));
        this.toggle.addEventListener('focusout', () => window.setTimeout(() => this.stepBack(), 0));
    }

    /**
     * Hides the bar while a module of the cart can be seen and shows it again when none can; the
     * page keeps its end clear of what shows (publishHeight()). The cart button in the buy bar
     * only turns invisible and keeps its room: the words and the buttons beside it stay where
     * they are, so nothing of the bar moves while the page scrolls.
     */
    stepBack() {
        if (!this.bar) {
            return;
        }
        const back = this.modulesInView.size > 0;
        if (back && (this.panel.open || this.toggle.contains(document.activeElement))) {
            return;
        }
        if (this.place) {
            this.toggle.classList.toggle(CartBar.STEPPED_BACK_CLASS, back);
            return;
        }
        if (this.bar.hidden !== back) {
            this.bar.hidden = back;
            this.publishHeight();
        }
    }

    /**
     * The height of the bar while it shows, 0 while the cart is empty.
     */
    publishHeight() {
        const height = this.bar ? this.bar.offsetHeight : 0;
        document.documentElement.style.setProperty(CartBar.HEIGHT_PROPERTY, height + 'px');
    }

    open() {
        if (this.panel.open) {
            return;
        }
        // a remembered cart before the answer: the panel stands in the hidden mini cart, empty;
        // it opens with the answer
        if (this.panel.closest('[hidden]')) {
            this.openWithTheAnswer = true;
            return;
        }
        this.panel.showModal();
        this.toggle.setAttribute('aria-expanded', 'true');
    }

    close() {
        if (!this.panel.open) {
            return;
        }
        this.panel.close();
        this.closed();
    }

    /**
     * The dialog is closed, whichever way: the toggle says so and takes the focus back, unless
     * the focus went somewhere else already. The browser hands it back to the toggle itself;
     * this covers a browser which leaves it on the body.
     */
    private closed() {
        this.toggle.setAttribute('aria-expanded', 'false');
        this.focusedRemove = -1;
        const active = document.activeElement;
        if (!active || active === document.body || this.panel.contains(active)) {
            this.toggle.focus({preventScroll: true});
        }
        this.stepBack();
    }

    /**
     * After an answer of the cart: the name of the toggle, the cart button of the buy bar with its
     * count and subtotal (MiniCart.ts writes those of the bar and the panel; the button left the
     * mini cart), the panel closed when the cart is empty, and the focus kept in the panel when
     * the item whose remove button had it is gone.
     */
    update(data: CartData | null) {
        if (!data) {
            return;
        }
        if (this.status && !this.toggle.hasAttribute('data-label')) {
            this.toggle.setAttribute('aria-label', CartFeedback.text(this.status, data));
        }

        const empty = !Array.isArray(data.cart) || data.cart.length === 0;
        // the panel closes before the cart button goes: closed() gives the focus to the button,
        // which the empty cart hides, so the focus moves on to what can be seen (review of #1894)
        const leavesThePanel = empty && this.panel.open;
        if (empty) {
            this.openWithTheAnswer = false;
            this.close();
        }
        if (this.place) {
            this.fill(cartItemCount(data), cartSubtotal(data), !empty);
        } else {
            this.name(cartItemCount(data), cartSubtotal(data));
        }
        // also on a page with the bar of its own: the next page may be an event page
        CartBar.remember(empty ? 0 : cartItemCount(data), cartSubtotal(data));
        if (empty) {
            if (leavesThePanel) {
                this.focusBesideTheEmptyCart();
            }
            return;
        }
        if (this.openWithTheAnswer) {
            this.openWithTheAnswer = false;
            this.open();
        }

        if (this.panel.open && this.focusedRemove >= 0 && !this.panel.contains(document.activeElement)) {
            const removes = this.panel.querySelectorAll<HTMLElement>(CartBar.REMOVE_SELECTOR);
            const next = removes[Math.min(this.focusedRemove, removes.length - 1)];
            (next ?? this.panel.querySelector<HTMLElement>(CartBar.CLOSE_SELECTOR))?.focus();
        }
    }

    /**
     * The last item went out of the open panel: the panel and the cart button are gone, and the
     * focus goes to what the visitor sees - the primary button of the buy bar on an event page
     * ("Select images"), otherwise the main heading of the page - a page without an h1 (a list
     * without its page heading) the <main> of the template -, which takes the focus with
     * tabindex="-1". Until the review of #1894 it stayed on the hidden button, for the browser on
     * the body.
     */
    private focusBesideTheEmptyCart() {
        const buyBar = this.place ? this.place.closest('.eg-bar') : null;
        const primary = buyBar
            ? Array.from(buyBar.querySelectorAll<HTMLButtonElement>('.eg-btn--primary')).find((button) => !button.disabled && !button.closest('[hidden]'))
            : undefined;
        const target: HTMLElement | null = primary ?? document.querySelector<HTMLElement>('main h1')
            ?? document.querySelector<HTMLElement>('h1') ?? document.querySelector<HTMLElement>('main');
        if (!target) {
            return;
        }
        if (!primary && !target.hasAttribute('tabindex')) {
            target.setAttribute('tabindex', '-1');
        }
        target.focus();
    }

    /**
     * The name of the toggle from its own words with the count and the subtotal ("Your Cart,
     * items: 3, 21.60 EUR") - what its glyph, badge and sum show. A toggle without them (an override)
     * is named by the sentence of the live region (update()).
     */
    private name(count: number, subtotal: string) {
        if (this.toggle.hasAttribute('data-label')) {
            this.toggle.setAttribute('aria-label', countText(this.toggle, 'data-label', count).replace('{subtotal}', subtotal));
        }
    }

    /**
     * The cart button of the buy bar with a cart of `count` items: the badge (0 for an empty cart,
     * as every count of the mini cart), the sum, the name; shown while the cart holds something.
     */
    private fill(count: number, subtotal: string, filled: boolean = true) {
        this.toggle.querySelectorAll(CartBar.COUNT_SELECTOR).forEach((element) => {
            element.textContent = String(count);
        });
        this.toggle.querySelectorAll(CartBar.SUBTOTAL_SELECTOR).forEach((element) => {
            element.textContent = subtotal;
        });
        this.name(count, subtotal);
        this.filled = filled;
        this.showWhenFilled();
    }

    /**
     * Shows the cart button of the buy bar while the cart holds something. While another dialog
     * of the page is open - the format dialog in which the buyer put the first item into the cart
     * or took the last one out - the button waits for it to close: it would move the words of the
     * buy mode beside it behind the dialog, a layout shift without an input of the visitor (the
     * answer of the cart comes later than 500 ms after the click), and it comes with the click
     * which closes the dialog instead.
     */
    private showWhenFilled() {
        if (this.toggle.hidden !== this.filled) {
            return;
        }
        if (this.otherDialogOpen()) {
            this.waitForTheDialog();
            return;
        }
        this.toggle.hidden = !this.filled;
    }

    private otherDialogOpen(): boolean {
        return Array.from(document.querySelectorAll('dialog[open]')).some((dialog) => dialog !== this.panel);
    }

    /**
     * Waits for the open dialogs of the page to close. The close event does not bubble, and the
     * overlay removes its dialog from the page right after close(), before the event comes: it is
     * heard on the dialog itself. The next answer of the cart looks again in any case.
     */
    private waitForTheDialog() {
        document.querySelectorAll('dialog[open]').forEach((dialog) => {
            if (dialog === this.panel || this.awaited.has(dialog)) {
                return;
            }
            this.awaited.add(dialog);
            dialog.addEventListener('close', () => {
                this.awaited.delete(dialog);
                window.setTimeout(() => this.showWhenFilled(), 0);
            }, {once: true});
        });
    }

    /**
     * The count and the subtotal of the last answer in this tab, null for an empty cart, none
     * remembered or a storage the browser does not grant.
     */
    static remembered(): {count: number, subtotal: string} | null {
        try {
            const value = JSON.parse(window.sessionStorage.getItem(CartBar.MEMORY_KEY) || 'null');
            const count = value ? parseInt(value.count, 10) : 0;
            return count > 0 ? {count: count, subtotal: String(value.subtotal ?? '')} : null;
        } catch (e) {
            return null;
        }
    }

    static remember(count: number, subtotal: string) {
        try {
            if (count > 0) {
                window.sessionStorage.setItem(CartBar.MEMORY_KEY, JSON.stringify({count: count, subtotal: subtotal}));
            } else {
                window.sessionStorage.removeItem(CartBar.MEMORY_KEY);
            }
        } catch (e) {
            // a storage the browser does not grant: the button comes with the answer of the cart
        }
    }

    /**
     * *View cart*: goes to the cart once no change of the cart is on its way any more.
     */
    private openCart(e: Event) {
        if (!this.cartProcessor.isBusy()) {
            return;
        }
        e.preventDefault();
        this.pendingHref = (e.currentTarget as HTMLAnchorElement).href;
    }
}
